import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Col, Form, Row } from 'reactstrap';
import { ErrorFormMessage } from 'components/ErrorFormMessage';
import { ErrorMessage } from '@hookform/error-message';
import { useForm } from 'react-hook-form';
import { notAllowedExtensions } from '../../../../shared/helpers/notAllowedExtensions';
import {
  FileSelectorWrapper,
  AttachmentContainer,
  AttachmentNameContainer,
  DeleteIcon,
  AttachmentImage,
  AttachmentLink,
  ClearButton,
  UploadButton
} from './styled';
import { useAttachments } from '../../../../../hooks/useAttachments';
import { ProductGallery } from 'views/CRM/Product/form/ProductDetails/ProductGallery';
import { DragAndDrop } from 'components/DragAndDrop';

const IMAGE_MAX_SIZE = 5 * 1024 * 1024;
const MAX_FILES = 10;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

export const Attachments = ({
  isFromProduct = false,
  entityId,
  attachments,
  onAdded,
  onDeleted,
  entityService,
  isButtonDisabled = false
}) => {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);

  const { errors } = useForm();

  const {
    getFileURL,
    removeAttachment,
    addAttachments,
    getFileExtensionFromName,
    getIconFromFileExtension,
    getFileNameFormatted
  } = useAttachments(entityService, entityId);

  const validateFiles = (fileList) => {
    if (!isFromProduct) return true;

    const filesArray = Array.from(fileList);

    if (filesArray.length > MAX_FILES) {
      toast.error(`Máximo de ${MAX_FILES} arquivos permitidos`);
      return false;
    }

    for (const file of filesArray) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error(
          `Formato não permitido: ${file.type}. Use apenas PNG, JPG ou JPEG`
        );
        return false;
      }

      if (file.size > IMAGE_MAX_SIZE) {
        toast.error(`Arquivo ${file.name} maior que 5MB`);
        return false;
      }
    }

    return true;
  };

  const handleDeleteAttachment = useCallback(
    async (attachmentId) => {
      if (window.confirm('Deseja realmente remover este anexo?') === false)
        return;
      const isDeleted = await removeAttachment(attachmentId);
      if (isDeleted) {
        toast.success('Anexo removido com sucesso');
        onDeleted(attachmentId);
        return;
      }
      return toast.error('Erro ao remover anexo');
    },
    [onDeleted, removeAttachment]
  );

  const changeFile = (validatedFiles, invalidatedFiles, event) => {
    if (validateFiles(validatedFiles)) {
      setFiles(validatedFiles);
    } else {
      event.target.value = null;
    }
  };

  const handleClearFiles = (event) => {
    event.target.form.reset();
    setFiles(null);
  };

  const getFileNamesToShow = () => {
    if (!files || !files.length) return null;
    if (files.length > 1) return `${files.length} arquivos selecionados`;
    return files[0].name;
  };

  const handleSubmitedForm = useCallback(
    async (event) => {
      try {
        const regex = /(?:\.([^.]+))?$/;

        setLoading(true);
        event.persist();

        if (!files || !files.length) {
          setLoading(false);
          return toast.error('Selecione um arquivo');
        }

        const formData = new FormData();
        let stopProcess = false;
        for (const file of files) {
          const ext = regex.exec(file.name)[1];

          if (!isFromProduct && notAllowedExtensions.includes(ext)) {
            stopProcess = true;
            return toast.error('Extensão ' + ext + ' não permitida');
          }
          formData.append('attachments', file, file.name);
        }

        if (stopProcess) {
          setLoading(false);
          return;
        }

        const addedAttachments = await addAttachments(formData);
        if (addedAttachments) {
          onAdded(addedAttachments);
          event.target.form.reset();
          setFiles(null);
          toast.success('Arquivo salvo com sucesso');
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error(error.response?.data?.message || 'Erro ao adicionar anexo');
      }
    },
    [addAttachments, onAdded, files, isFromProduct]
  );

  const isImageFile = (filename) => {
    const ext = getFileExtensionFromName(filename).toLowerCase();
    return ['jpg', 'jpeg', 'png'].includes(ext);
  };

  return (
    <>
      <FileSelectorWrapper>
        <Form className="needs-validation" encType="multipart/form-data">
          <Row className="mb-3">
            <Col sm="12">
              <label className="form-control-label d-block">
                {isFromProduct
                  ? 'Imagens do Produto (PNG, JPG, JPEG - máx. 5MB)*'
                  : 'Arquivos ( pdf, doc, docx, png, jpg, jpeg )*'}
              </label>
              <div
                style={{
                  width: '20rem'
                }}
              >
                <DragAndDrop
                  acceptedFileExtensions={
                    isFromProduct
                      ? '.png, .jpeg, .jpg'
                      : '.pdf, .doc, .docx, .png, .jpg, .jpeg'
                  }
                  multiple
                  id="attachments"
                  name="attachments"
                  changeFile={changeFile}
                  description={
                    isFromProduct
                      ? 'Clique ou arraste para adicionar imagens (máx. 10)'
                      : 'Clique ou arraste para adicionar arquivos'
                  }
                  isDisabled={isButtonDisabled}
                />
              </div>
              <ErrorMessage
                errors={errors}
                name="attachments"
                render={({ message }) => ErrorFormMessage(message)}
              />
            </Col>
            {files && files.length ? (
              <AttachmentContainer>
                <p>{getFileNamesToShow()}</p>
                <ClearButton
                  size="sm"
                  type="button"
                  color="danger"
                  title="Remover arquivo(s) selecionado(s)"
                  onClick={handleClearFiles}
                >
                  Limpar
                </ClearButton>
                <UploadButton
                  size="sm"
                  type="button"
                  color="success"
                  title="Adicionar arquivo(s) selecionado(s)"
                  onClick={handleSubmitedForm}
                  disabled={loading}
                >
                  {loading ? 'Aguarde...' : 'Fazer Upload'}
                </UploadButton>
              </AttachmentContainer>
            ) : null}
          </Row>
        </Form>
      </FileSelectorWrapper>

      {attachments && !!attachments.length && (
        <>
          {isFromProduct ? (
            <ProductGallery
              images={attachments.filter((att) => isImageFile(att.name))}
              onDelete={handleDeleteAttachment}
              productId={entityId}
              productService={entityService}
            />
          ) : (
            <div
              className="mt-3"
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                boxSizing: 'border-box',
                flexWrap: 'wrap',
                width: '100%'
              }}
            >
              {attachments.map((att) => (
                <AttachmentNameContainer key={att.id} title={att.name}>
                  <AttachmentImage
                    src={getIconFromFileExtension(
                      getFileExtensionFromName(att.name)
                    )}
                    width="24px"
                    height="24px"
                    alt={att.name}
                  />
                  <AttachmentLink
                    href={getFileURL(att.path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={true}
                    className="small line-height-0"
                  >
                    {getFileNameFormatted(att.name)}
                  </AttachmentLink>
                  <DeleteIcon
                    type="button"
                    onClick={() => handleDeleteAttachment(att.id)}
                    className="delete-icon"
                  />
                </AttachmentNameContainer>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};
